// src/components/ResultPage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ResultQuiz from "./ResultQuiz";
import ResultSurvey from "./ResultSurvey";
import { Breadcrumb } from "components";
import { translate } from "react-switch-lang";
import "./ResultPage.scss";
import LeaderBoard from "./LeaderBoard";
import api from "../api/api";
import { Spinner } from "reactstrap";

const ResultPage = ({ t }) => {
  const { id, type } = useParams();
  const [results, setResults] = useState(null);
  const [score, setScore] = useState(null);
  const [maxScore, setMaxScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRole = localStorage.getItem("userRole");
  const title = localStorage.getItem("Title");
  const userId = localStorage.getItem("userId");

  const isAdmin = userRole === "ADMIN";
  const handleExport = (format) => {
    let dataToExport;

    // Admin exports all attempts, user exports only their own
    if (type === "quiz") {
      dataToExport = isAdmin ? results.adminData : results.userData;
    } else {
      dataToExport = results.surveyData;
    }

    if (!dataToExport) return;

    // ---------------- JSON EXPORT ----------------
    if (format === "json") {
      const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${type}-results-${id}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    // ---------------- CSV EXPORT ----------------
    if (format === "csv") {
      const csvRows = [];

      if (type === "quiz") {
        const header = [
          "Username",
          "Attempt",
          "Question",
          "Option 1",
          "Option 2",
          "Option 3",
          "Option 4",
          "Correct Answer(s)",
          "You Selected",
          "Score",
          "Max Score",
          "Submitted At",
        ];
        csvRows.push(header.join(","));

        const processEntry = (entry) => {
          if (!entry?.answers || Object.keys(entry.answers).length === 0)
            return;

          const {
            username,
            attemptNumber = "",
            score,
            maxScore,
            submittedAt,
            answers,
          } = entry;

          Object.entries(answers).forEach(([question, detail]) => {
            const optionList = detail?.choices || []; // FIX: backend uses "choices"

            const options = optionList.map((opt) => opt.text);

            const corrects = optionList
              .filter((opt) => opt.correct)
              .map((opt) => opt.text)
              .join(" / ");

            const selected = Array.isArray(detail?.selectedOptions)
              ? detail.selectedOptions.join(" / ")
              : detail?.selectedOptions || "";

            const row = [
              username,
              attemptNumber,
              question,
              options[0] || "",
              options[1] || "",
              options[2] || "",
              options[3] || "",
              corrects,
              selected,
              score,
              maxScore,
              submittedAt,
            ];

            csvRows.push(row.map((v) => `"${v}"`).join(","));
          });
        };

        const entries = Array.isArray(dataToExport)
          ? dataToExport
          : [dataToExport];

        entries.forEach((e) => processEntry(e));
      }

      // ---------------- SURVEY CSV ----------------
      else {
        const header = ["Question", "Type", "Result"];
        csvRows.push(header.join(","));

        dataToExport.forEach((entry) => {
          const resultString = Object.entries(entry.result || {})
            .map(([option, percent]) => `${option} (${percent}%)`)
            .join(" | ");

          const row = [entry.question, entry.type, resultString];
          csvRows.push(row.map((v) => `"${v}"`).join(","));
        });
      }

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${type}-results-${id}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    let ignore = false; // prevent race condition

    const fetchData = async () => {
      try {
        const { data } = await api.fetchResults(type, id, userRole, userId);
        if (ignore) return;

        if (type === "quiz") {
          setResults({
            userData: data.data,
            adminData: isAdmin ? data.data : null,
          });
        } else {
          setResults({ surveyData: data.data });
        }
      } catch (err) {
        if (!ignore) console.error("Failed to fetch results");
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchData();
    return () => {
      ignore = true;
    }; // cleanup
  }, [id, type]); // 👈 only depend on id & type

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner
          animation="border"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        />
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title={t(title)} />
      <div className="result-container">
        {type === "quiz" && (
          <div className="leaderboard-section">
            <LeaderBoard quizId={id} />
          </div>
        )}

        {type === "quiz" ? (
          <ResultQuiz
            userdata={results?.userData}
            isAdmin={isAdmin}
            adminData={results?.adminData}
          />
        ) : (
          <ResultSurvey results={results} isAdmin={isAdmin} />
        )}

        {results && (
          <div className="export-buttons">
            <button onClick={() => handleExport("csv")}>
              📥 Export as CSV
            </button>
            <button onClick={() => handleExport("json")}>
              📥 Export as JSON
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default translate(ResultPage);
